import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GamificationPanel = ({ achievements, givingStreak, leaderboardPosition, totalPoints }) => {
  const getStreakIcon = (streak) => {
    if (streak >= 30) return 'Flame';
    if (streak >= 7) return 'Zap';
    return 'Calendar';
  };

  const getStreakColor = (streak) => {
    if (streak >= 30) return 'text-error';
    if (streak >= 7) return 'text-warning';
    return 'text-primary';
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
            <Icon name={getStreakIcon(givingStreak)} size={20} className={getStreakColor(givingStreak)} />
          </div>
          <div className="text-2xl font-bold text-card-foreground">{givingStreak}</div>
          <div className="text-sm text-muted-foreground">Day Streak</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center mx-auto mb-2">
            <Icon name="Trophy" size={20} className="text-warning" />
          </div>
          <div className="text-2xl font-bold text-card-foreground">#{leaderboardPosition}</div>
          <div className="text-sm text-muted-foreground">Leaderboard</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
            <Icon name="Star" size={20} className="text-secondary" />
          </div>
          <div className="text-2xl font-bold text-card-foreground">{totalPoints?.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground">Points</div>
        </div>
      </div>
      {/* Achievements */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Achievements</h3>
            <p className="text-sm text-muted-foreground">Your giving milestones</p>
          </div>
          <Button variant="ghost" size="sm" iconName="ExternalLink">
            View All
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {achievements?.map((achievement) => (
            <div
              key={achievement?.id}
              className={`p-4 rounded-lg border transition-smooth ${
                achievement?.unlocked
                  ? 'border-success bg-success/5' :'border-border bg-muted/30'
              }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  achievement?.unlocked
                    ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  <Icon name={achievement?.icon} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`font-medium text-sm ${
                    achievement?.unlocked ? 'text-card-foreground' : 'text-muted-foreground'
                  }`}>
                    {achievement?.title}
                  </h4>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {achievement?.description}
                  </p>
                </div>
              </div>
              
              {achievement?.progress !== undefined && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="text-muted-foreground">
                      {achievement?.progress}/{achievement?.target}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-smooth ${
                        achievement?.unlocked ? 'bg-success' : 'bg-primary'
                      }`}
                      style={{
                        width: `${Math.min((achievement?.progress / achievement?.target) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">Recent Achievements</h3>
          <p className="text-sm text-muted-foreground">Your latest milestones</p>
        </div>

        <div className="space-y-3">
          {achievements?.filter(a => a?.unlocked && a?.unlockedDate)?.sort((a, b) => new Date(b.unlockedDate) - new Date(a.unlockedDate))?.slice(0, 3)?.map((achievement) => (
              <div key={achievement?.id} className="flex items-center space-x-3 p-3 bg-success/5 border border-success/20 rounded-lg">
                <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name={achievement?.icon} size={16} className="text-success" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-card-foreground text-sm">{achievement?.title}</p>
                  <p className="text-xs text-muted-foreground">
                    Unlocked {new Date(achievement.unlockedDate)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                <div className="text-success font-medium text-sm">
                  +{achievement?.points} pts
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default GamificationPanel;